import useSWR, { type KeyedMutator } from "swr";
import useSWRMutation from "swr/mutation";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { useApiClient } from "@/hooks/useApiClient";
import {
  getConnection,
  SALESFORCE_CONNECTION_ENDPOINT,
} from "@/features/salesforceIntegration/api/getConnection";
import { deleteConnection } from "@/features/salesforceIntegration/api/deleteConnection";
import { getAuthorizeUrl } from "@/features/salesforceIntegration/api/getAuthorizeUrl";
import { deriveConnectionStatus } from "@/features/salesforceIntegration/utils/deriveConnectionStatus";
import {
  type SalesforceConnectionResponse,
  type SalesforceConnectionStatus,
} from "@/features/salesforceIntegration/types";

export interface UseSalesforceConnectionOptions {
  /** `false`の場合はフェッチしない（LaunchDarklyフラグが無効な画面での無駄なリクエスト抑止用）。 */
  enabled?: boolean;
}

export interface UseSalesforceConnectionResult {
  connection: SalesforceConnectionResponse | undefined;
  isLoading: boolean;
  connectionStatus: SalesforceConnectionStatus;
  startAuthorization: () => Promise<void>;
  isStartingAuthorization: boolean;
  disconnect: () => Promise<void>;
  isDisconnecting: boolean;
  mutate: KeyedMutator<SalesforceConnectionResponse>;
}

/**
 * Salesforce接続状態の取得と、認証開始・連携解除の操作をまとめたhook。
 * ユーザー設定画面（機能4）と連携ボタンの表示ゲート（機能5、`hasContract`）の両方から使う。
 */
export const useSalesforceConnection = (
  options: UseSalesforceConnectionOptions = {}
): UseSalesforceConnectionResult => {
  const { enabled = true } = options;
  const { apiClient } = useApiClient();

  const { data, isLoading, mutate } = useSWR<SalesforceConnectionResponse>(
    enabled ? SALESFORCE_CONNECTION_ENDPOINT : null,
    async () => await getConnection(apiClient),
    { revalidateOnFocus: false }
  );

  const authorizeUrlMutation = useSWRMutation(
    "/salesforce/oauth/authorize-url/",
    async () => await getAuthorizeUrl(apiClient)
  );

  const disconnectMutation = useSWRMutation(
    SALESFORCE_CONNECTION_ENDPOINT,
    async () => {
      await deleteConnection(apiClient);
    },
    { revalidate: false }
  );

  const startAuthorization = async (): Promise<void> => {
    try {
      const { authorizeUrl } = await authorizeUrlMutation.trigger();
      window.location.assign(authorizeUrl);
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Salesforce連携の開始に失敗しました");
    }
  };

  const disconnect = async (): Promise<void> => {
    try {
      await disconnectMutation.trigger();
      await mutate();
      toast.success("Salesforceとの連携を解除しました");
    } catch (error) {
      Sentry.captureException(error);
      toast.error("連携の解除に失敗しました");
    }
  };

  return {
    connection: data,
    isLoading,
    connectionStatus: deriveConnectionStatus(data),
    startAuthorization,
    isStartingAuthorization: authorizeUrlMutation.isMutating,
    disconnect,
    isDisconnecting: disconnectMutation.isMutating,
    mutate,
  };
};
